import React from 'react';
import { Link } from 'react-router-dom';
import AttrValue from './AttrValue';
import composeLine, { memberText } from './composeLine';
import formatScalar from './formatValue';
import evaluateFormula, { formatSigned } from '../../utils/displayFormula';
import JsonTable from '../JsonTable';
import RichText from './RichText';
import {
  segmentForFamilyKey, filterRouteFor, reverseBucketRoute, getFamilyByKey,
} from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';
import displayFormatText from '../../utils/displayFormat';

/**
 * One derived detail section (05 §2.1, §2.2), dispatched on its render style.
 *
 * A style lands with the Family that declares it, so each arrives with a real
 * render to grade it against rather than as speculative code. `rows` (the
 * default `labeled-rows` group style, and the ungrouped scalar run) and `prose`
 * came with Languages and Rulebooks; `composed-line`, `abbrev-list` and
 * `link-list` with Spells; `badge-links` and `table` with Deities and Skills;
 * `signed-list` with Races; and `stat-grid` — the score-plus-computed-modifier
 * grid, whose arithmetic is AUTHORED and evaluated by `displayFormula.js` —
 * with Monsters. `inline-expand` and `bucket-by-link-data` are what Classes
 * still owes.
 *
 * `IMPLEMENTED_STYLES` is exported so a conversion cannot silently drop a
 * section: `familyRegistry.test.js` asserts every style the converted Families
 * derive is in this set, and the next conversion's own test extends it.
 *
 * EVERY SECTION AND ROW SELF-HIDES WHEN EMPTY (05 §5, G4). `StatRow`'s
 * self-hiding becomes the rule rather than the accident, which is what stops the
 * always-on "Additional Information" headings from rendering over nothing.
 *
 * TWO MARKUPS, ONE COMPONENT. A Family's `detail_style` (v2.4) decides whether
 * a section renders as a `.card` with an `h2.section-header` or as rows inside
 * a `.stat-block` — 02b Part 5 item 2's two visual languages. It is threaded
 * through as `variant` rather than branched on per style, because it changes
 * the wrapper and never the content.
 */

export const IMPLEMENTED_STYLES = new Set([
  'rows', 'prose', 'composed-line', 'abbrev-list', 'link-list',
  // Added with Feats / Skills / Deities at R9.
  'badge-links', 'table',
  // Added with Races at R10 — the ability-modifier sentence.
  'signed-list',
  // Added with Monsters at R11 — the score-plus-computed-modifier grid.
  'stat-grid',
  // Added with Classes at R12 — the two reverse-section styles and the
  // reference flavor of the composed line (the requirements sentence).
  'inline-expand', 'bucket-by-link-data',
]);

/**
 * Styles that render their OWN container and therefore cannot be a row inside a
 * stat block (05 §1.3's two visual languages).
 *
 * `prose` was the whole of this set until R12, because a description card is a
 * card on every shipped page, stat-block or not. Classes adds two: an accordion
 * list and a grid of level links are cards on the shipped class page for the
 * same reason — neither is a `Label: value` pair, so there is no stat-block
 * markup for them to take.
 */
export const CARD_ONLY_STYLES = new Set(['prose', 'inline-expand', 'bucket-by-link-data']);

function hasValue(v) {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** The card/stat-block wrapper a section renders into. */
function Block({ variant, heading, children }) {
  if (variant === 'stat-block') {
    return (
      <>
        <hr className="stat-block-divider" />
        {heading && <div className="stat-block-section">{heading}</div>}
        {children}
      </>
    );
  }
  return (
    <div className="card">
      {heading && <h2 className="section-header">{heading}</h2>}
      {children}
    </div>
  );
}

/**
 * THE GLUE SLOTS of a `composed-line` reference (05 §2.2, inventory hard-thing
 * #2).
 *
 * A prestige class's requirements are a SENTENCE, not a list: "Base Attack
 * Bonus +5, Knowledge (arcana) 8 ranks, and any two metamagic feats". The
 * import kept the connective prose beside each link rather than throwing it
 * away, as four link-data fields — and these four names ARE the platform
 * vocabulary of this style, named as such by 05 §2.2 ("reads
 * `text_before`/`text_after`/`remove_comma` link data"), not by any family.
 *
 * Everything NOT in this set is ordinary link data and renders by the same rule
 * `linkDataText` states one level up: a value, then its label. `6 ranks` is
 * exactly that — the `ranks` field and its own label, lower-cased, which is why
 * nothing here knows what a skill rank is.
 */
const GLUE_SLOTS = new Set(['text_before', 'text_after', 'extra', 'remove_comma']);

export function composedReferenceParts(attr, targets) {
  const extras = (attr.linkData || []).filter((f) => !GLUE_SLOTS.has(f.key));
  return (Array.isArray(targets) ? targets : []).map((target, i) => ({
    key: target.slug || i,
    // The flag rides on the FOLLOWING item, which is what the shipped page
    // reads: `{i > 0 && !race.remove_comma && ' or '}`.
    separator: i > 0 && !target.remove_comma,
    before: target.text_before || '',
    name: target.name,
    slug: target.slug,
    parenthetical: target.extra ? ` (${target.extra})` : '',
    qualifiers: extras
      .filter((f) => target[f.key] !== undefined && target[f.key] !== null && target[f.key] !== '')
      .map((f) => ` ${target[f.key]} ${String(f.label || f.key).toLowerCase()}`)
      .join(''),
    after: target.text_after || '',
  }));
}

/** The composed requirements sentence, as text — the seam the unit tests read. */
export function composedReferenceText(attr, targets, separator) {
  return composedReferenceParts(attr, targets)
    .map((p) => `${p.separator ? separator : ''}${p.before}${p.name}${p.parenthetical}${p.qualifiers}${p.after}`)
    .join('');
}

function ComposedReference({ attr, value, from, listFrom, separator }) {
  const segment = segmentForFamilyKey(attr.targetFamily, getActiveSystem());
  return (
    <>
      {composedReferenceParts(attr, value).map((part) => (
        <span key={part.key}>
          {part.separator && separator}
          {part.before}
          {segment && part.slug ? (
            <Link
              to={`/${segment}?slug=${encodeURIComponent(part.slug)}`}
              state={{ from, listFrom }}
              className="class-level-link"
            >
              {part.name}
            </Link>
          ) : part.name}
          {part.parenthetical}
          {part.qualifiers}
          {part.after}
        </span>
      ))}
    </>
  );
}

const DEFAULT_COMPOSED_SEPARATOR = ', ';

/** One `Label: value` pair, in whichever markup the Family's detail speaks. */
function Row({ attr, value, via, from, listFrom, variant }) {
  // A PROSE attribute inside a labelled-rows group is still prose: the shipped
  // class page prints the requirements blurb as a paragraph under the
  // Requirements rows, with no label of its own, because the paragraph IS the
  // requirement. Labelling it would be the mirror of the `isProse` rule the
  // ungrouped scan already applies.
  if (attr.isProse) {
    return attr.datatype === 'richtext'
      ? <RichText className="prose-content" html={value} />
      : <div className={attr.preserveWhitespace ? 'text-secondary whitespace-pre-wrap' : 'text-secondary'}>{value}</div>;
  }

  const body = attr.renderStyle === 'composed-line' && attr.kind === 'reference' ? (
    <ComposedReference
      attr={attr}
      value={value}
      from={from}
      listFrom={listFrom}
      separator={(attr.renderConfig || {}).separator || DEFAULT_COMPOSED_SEPARATOR}
    />
  ) : <AttrValue attr={attr} value={value} via={via} from={from} listFrom={listFrom} />;

  if (variant === 'stat-block') {
    return (
      <div className="stat-block-row">
        <span className="stat-block-label">{attr.label}:</span>
        <span className="stat-block-value">{body}</span>
      </div>
    );
  }
  return (
    <div>
      <strong>{attr.label}:</strong>{' '}
      {body}
    </div>
  );
}

function Rows({ section, values, from, listFrom, variant }) {
  const present = section.members.filter((attr) => hasValue(values[attr.key]));
  if (!present.length) return null;
  return (
    <Block variant={variant} heading={section.heading}>
      <div className={variant === 'stat-block' ? 'space-y-1' : 'space-y-3'}>
        {present.map((attr) => (
          <Row
            key={attr.key}
            attr={attr}
            value={values[attr.key]}
            // A value that links through a reference beside it (issue 818, `render_config.link_via`).
            via={(attr.renderConfig || {}).link_via ? values[attr.renderConfig.link_via] : undefined}
            from={from}
            listFrom={listFrom}
            variant={variant}
          />
        ))}
      </div>
    </Block>
  );
}

function Prose({ section, values }) {
  const value = values[section.attr.key];
  if (!hasValue(value)) return null;
  return (
    <div className="card">
      <h2 className="section-header">{section.heading}</h2>
      {section.attr.datatype === 'richtext' ? (
        <RichText className="prose-content" html={value} />
      ) : (
        // 05 §2.1's `preserve_whitespace`, derived from the description
        // register rather than authored: `SkillDetail` wrapped its Description
        // card in `whitespace-pre-wrap` and flowed Action / Try Again /
        // Special / Synergy as ordinary paragraphs, which is the whole of the
        // rule. Preserving line breaks everywhere would re-wrap half the skill
        // corpus; preserving them nowhere would run the shipped Description
        // card's paragraphs together.
        <div className={section.attr.preserveWhitespace
          ? 'text-secondary whitespace-pre-wrap' : 'text-secondary'}>{value}</div>
      )}
    </div>
  );
}

/**
 * A structured-table attribute, rendered by `JsonTable` (05 §2.1, gap G2).
 *
 * `JsonTable` is the renderer for the `table` datatype and is kept exactly as
 * it is: it was already entity-agnostic — it accepts the fixed document schema
 * and knows nothing about skills — so promoting it is zero new code and its ten
 * existing tests keep passing. This is the one place in the vocabulary where
 * "no type-specific code" would otherwise have been false.
 */
function TableSection({ section, values }) {
  const value = values[section.attr.key];
  if (!hasValue(value)) return null;
  return (
    <div className="space-y-6">
      <JsonTable tableData={value} title={section.heading} />
    </div>
  );
}

/**
 * A cluster of linked badges (05 §2.2, v2.5).
 *
 * The members are rendered as ONE set under the group's heading, in declaration
 * order, and a member links or does not according to what it is — a reference
 * whose target family can be filtered on a list page links there, a plain text
 * value cannot and stays a bare badge. That degrade is not a fallback path: it
 * is half the data. 19 of a Pathfinder deity's 81 distinct subdomain names come
 * from books outside the imported domain list, and the shipped page rendered
 * them as plain badges beside the linked ones, which is exactly what "the same
 * cluster, some of it linkable" means.
 */
function BadgeLinks({ section, values, from, listFrom }) {
  const config = section.config || {};
  const tone = config.color || 'badge-gray';

  const badges = [];
  for (const attr of section.members) {
    const value = values[attr.key];
    if (!hasValue(value)) continue;
    const route = attr.kind === 'reference'
      ? filterRouteFor(attr.targetFamily, config.link_filter_key, getActiveSystem())
      : null;
    for (const item of Array.isArray(value) ? value : [value]) {
      const name = item && typeof item === 'object' ? item.name : item;
      if (!name) continue;
      const slug = item && typeof item === 'object' ? item.slug : null;
      badges.push({ key: `${attr.key}:${slug || name}`, name, slug, route });
    }
  }
  if (!badges.length) return null;

  return (
    <div className="card">
      <h2 className="section-header">{section.heading}</h2>
      <div className="flex flex-wrap gap-2">
        {badges.map((badge) => (badge.route && badge.slug ? (
          <Link
            key={badge.key}
            to={`/${badge.route.segment}?${badge.route.urlKey}=${encodeURIComponent(badge.slug)}`}
            state={{ from, listFrom }}
            className={`badge ${tone} hover:underline`}
            title={`View ${badge.name} ${section.heading.toLowerCase()} spells`}
          >
            {badge.name}
          </Link>
        ) : (
          <span key={badge.key} className={`badge ${tone}`}>{badge.name}</span>
        )))}
      </div>
    </div>
  );
}

/**
 * The ability-modifier SENTENCE (05 §2.1, v2.6) — a signed list.
 *
 * `RaceDetail`'s `formatAbilityModifiers` is what this replaces, and 02b Part 5
 * item 13 is emphatic that it is "a sentence, not a grid". Three rules, none of
 * them about races:
 *
 *   - a member with no value, or a zero, contributes nothing. Every race has
 *     six ability columns and almost none of them are interesting;
 *   - a member that does renders `+2 Con` / `−2 Cha` — the sign first, the
 *     label after, and a real U+2212 MINUS SIGN for negatives, which is what
 *     the shipped page printed;
 *   - a `template_member` renders its own sentence from a value inside a
 *     structured attribute: Pathfinder's `{"flexible": 2}` becomes "+2 to any
 *     one ability score", which is a bonus the player places rather than one
 *     the race fixes.
 *
 * Whole-group self-hiding falls out of the same rule as everything else: a race
 * with no modifiers at all produces no parts and therefore no row.
 */
const MINUS = '−';

export function signedListText(section, values) {
  const parts = [];
  for (const attr of section.members) {
    if (attr.datatype !== 'number') continue;
    const n = Number(values[attr.key]);
    if (!Number.isFinite(n) || n === 0) continue;
    parts.push(`${n > 0 ? '+' : MINUS}${Math.abs(n)} ${attr.label}`);
  }
  for (const template of (section.config || {}).template_members || []) {
    const source = values[template.key];
    const raw = template.sub_key
      ? (source && typeof source === 'object' ? source[template.sub_key] : undefined)
      : source;
    if (raw === undefined || raw === null || raw === '') continue;
    parts.push(String(template.template).replace('{value}', String(raw)));
  }
  return parts.join(', ');
}

function SignedList({ section, values, variant }) {
  const text = signedListText(section, values);
  if (!text) return null;
  if (variant === 'stat-block') {
    return (
      <Block variant={variant} heading={null}>
        <div className="stat-block-row">
          <span className="stat-block-label">{section.heading}:</span>
          <span className="stat-block-value">{text}</span>
        </div>
      </Block>
    );
  }
  return (
    <div className="card">
      <div><strong>{section.heading}:</strong> {text}</div>
    </div>
  );
}

/**
 * The N-up score grid, each cell a value and a modifier COMPUTED from it
 * (05 §2.1, inventory hard-thing #3).
 *
 * `MonsterDetail.AbilityScore` is what this replaces, and the interesting half
 * is that the arithmetic is authored: the group carries
 * `formula: "floor((value - 10) / 2)"` and `displayFormula.js` evaluates it
 * against a scope whose `value` is the member's own. Nothing here knows that a
 * d20 ability modifier is a score minus ten over two, which is the point — a
 * system whose modifier is a different function of the score needs no code.
 *
 * A member with no value, or a zero, renders no cell. That is the shipped rule
 * (`if (!score) return null`) and it matters: 27 dnd-3.5 monsters carry `N/A`
 * for Constitution, which lands as no value at all under MRCS.
 *
 * A group with no formula renders the bare values, so the style degrades to "a
 * grid of numbers" rather than failing.
 */
function StatGrid({ section, values, variant }) {
  const cells = [];
  for (const attr of section.members) {
    const raw = values[attr.key];
    const n = hasValue(raw) ? Number(raw) : NaN;
    // A score the creature does not have (an undead's or a construct's Con)
    // keeps its cell and reads "—", as the books print it, so the grid stays
    // six-up and in order rather than closing up to five.
    if (!Number.isFinite(n) || n === 0) {
      cells.push({ key: attr.key, label: attr.label, score: '—', modifier: null, none: true });
      continue;
    }
    const modifier = section.formula ? evaluateFormula(section.formula, { ...values, value: n }) : null;
    cells.push({ key: attr.key, label: attr.label, score: n, modifier });
  }
  if (cells.every((cell) => cell.none)) return null;

  return (
    <Block variant={variant} heading={section.heading}>
      <div className="stat-block-abilities">
        {cells.map((cell) => (
          <div key={cell.key} className="stat-block-ability">
            <span className="stat-block-ability-name">{cell.label}</span>
            <span className="stat-block-ability-score">{cell.score}</span>
            {cell.modifier !== null && (
              <span className="stat-block-ability-mod">{formatSigned(cell.modifier)}</span>
            )}
          </div>
        ))}
      </div>
    </Block>
  );
}

/** The composed line, rendered from the group's authored `pattern`. */
export function ComposedLine({ section, values }) {
  const text = composeLine((section.config || {}).pattern, values);
  if (!text) return null;
  return <span>{text}</span>;
}

/**
 * One `parts` run: each part's value, its optional `suffix`, joined by the
 * group's `separator`. A part with no value takes its suffix with it, which is
 * the same rule `composeLine`'s wrappers follow.
 */
export function partsText(parts, separator, values, members) {
  return (parts || [])
    .map((part) => {
      // A number part is spelled by its attribute's own format: a `signed` save reads `+6`.
      const attr = (members || []).find((m) => m.key === part.attr);
      const raw = values[part.attr];
      const text = attr && attr.datatype === 'number' && hasValue(raw) ? formatScalar(attr, raw) : memberText(raw);
      return text ? `${part.prefix || ''}${text}${part.suffix || ''}` : '';
    })
    .filter(Boolean)
    .join(separator === undefined ? ' ' : separator);
}

/**
 * THE COMPOSED LINE HAS THREE AUTHORED SHAPES, and R11 is where the second and
 * third arrive — authoring call 92 deferred them to the slice with a render to
 * grade them against, and Monsters declares both.
 *
 * Which shape a group is, is decided by which config key it carries, so nothing
 * is authored twice:
 *
 *   `pattern`   one line, no label — the spell's `Evocation (Fire) [Fire]` and
 *               the monster's `Large, Outsider, Always lawful evil` subtitle.
 *   `parts`     ONE LABELLED ROW whose value is the parts joined: the monster's
 *               `Space/Reach: 10 ft. / 5 ft.`, which the shipped page rendered
 *               inside the Combat block as an ordinary row and not as a section
 *               of its own — so this shape takes the group's label as its ROW
 *               label and contributes no heading.
 *   `lines`     SEVERAL labelled rows under the group's heading: the monster's
 *               `Saving Throws` block, whose three rows are `Fort`, `Ref` and
 *               `Will` and whose values are a save plus its `*_extra` tail.
 *
 * The `*_extra` tail is why `lines` exists rather than three sibling groups:
 * `fort_save_extra` is not a fact of its own, it is a qualifier on the save, and
 * a group per save would put three headings where the page has one.
 */
function ComposedLineSection({ section, values, variant }) {
  const config = section.config || {};

  if (Array.isArray(config.lines)) {
    const rows = config.lines
      .map((line) => ({ label: line.label, text: partsText(line.parts, config.separator, values, section.members) }))
      .filter((row) => row.text);
    if (!rows.length) return null;
    return (
      <Block variant={variant} heading={section.heading}>
        <div className={variant === 'stat-block' ? 'flex flex-wrap gap-x-6 gap-y-1' : 'space-y-3'}>
          {rows.map((row) => (variant === 'stat-block' ? (
            <div key={row.label} className="stat-block-row" style={{ marginBottom: 0 }}>
              <span className="stat-block-label">{row.label}:</span>
              <span className="stat-block-value">{row.text}</span>
            </div>
          ) : (
            <div key={row.label}><strong>{row.label}:</strong> {row.text}</div>
          )))}
        </div>
      </Block>
    );
  }

  if (Array.isArray(config.parts)) {
    const text = partsText(config.parts, config.separator, values, section.members);
    if (!text) return null;
    if (variant === 'stat-block') {
      return (
        <div className="stat-block-row">
          <span className="stat-block-label">{section.heading}:</span>
          <span className="stat-block-value">{text}</span>
        </div>
      );
    }
    return <div><strong>{section.heading}:</strong> {text}</div>;
  }

  const text = composeLine(config.pattern, values);
  if (!text) return null;
  if (variant === 'stat-block') {
    return (
      <Block variant={variant} heading={section.heading}>
        <div className="stat-block-row"><span className="stat-block-value">{text}</span></div>
      </Block>
    );
  }
  return <Block variant={variant} heading={section.heading}><div>{text}</div></Block>;
}

/**
 * The abbreviation cluster: `V, S, M` from the truthy boolean members, `—` when
 * none is, and every non-boolean member appended as a muted parenthetical.
 *
 * That last clause is what `SpellDetail` spelled `({spell.extra_components})`,
 * and stating it as a rule of the STYLE rather than a line of spell code is the
 * whole exercise: a group of booleans-with-abbreviations plus a free-text tail
 * is a shape, not a spell concept.
 */
export function abbrevText(members, values) {
  const flags = members
    .filter((m) => m.datatype === 'boolean' && m.abbr && values[m.key])
    .map((m) => m.abbr);
  return flags.join(', ') || '—';
}

function AbbrevList({ section, values, variant }) {
  const text = abbrevText(section.members, values);
  const tails = section.members
    .filter((m) => m.datatype !== 'boolean' && hasValue(values[m.key]))
    .map((m) => String(values[m.key]));

  const body = (
    <>
      {text}
      {tails.map((tail) => (
        <span key={tail} className="text-muted ml-1">({tail})</span>
      ))}
    </>
  );

  if (variant === 'stat-block') {
    return (
      <Block variant={variant} heading={null}>
        <div className="stat-block-row">
          <span className="stat-block-label">{section.heading}:</span>
          <span className="stat-block-value">{body}</span>
        </div>
      </Block>
    );
  }
  return (
    <div className="card">
      <div><strong>{section.heading}:</strong> {body}</div>
    </div>
  );
}

/**
 * A reference set as "target → its link data" pairs (05 §2.2).
 *
 * The link-data rule, stated once so no Family has to restate it: the
 * lowest-`display_order` link-data field is the VALUE, and every other non-empty
 * one is a parenthesized qualifier after it. A spell's Wizard 3, and its
 * Shugenja `4 (Agasha School)`, are both that rule — and so are a feat's skill
 * ranks, which is why it is worth having rather than an `item_pattern` string
 * per Family.
 *
 * A target links when its family has a route of its own and renders as plain
 * text when it does not — the same data-driven degrade `AttrValue` applies, and
 * the reason spell domains are labels while spell classes are links without
 * anything here knowing what a domain is.
 */
export function linkDataText(attr, target) {
  const fields = (attr.linkData || []).filter((f) => target[f.key] !== undefined
    && target[f.key] !== null && target[f.key] !== '');
  if (!fields.length) return '';
  const [primary, ...qualifiers] = fields;
  return `${target[primary.key]}${qualifiers.map((f) => ` (${target[f.key]})`).join('')}`;
}

function LinkList({ section, values, from, listFrom, variant }) {
  const targets = values[section.attr.key];
  if (!Array.isArray(targets) || !targets.length) return null;
  const segment = segmentForFamilyKey(section.attr.targetFamily, getActiveSystem());

  const item = (target, i) => {
    const spelled = displayFormatText(section.attr, target);
    if (spelled !== null) {
      return (
        <span key={target.slug || i} className="stat-block-row" style={{ marginBottom: 0 }}>
          <span className="stat-block-value">{spelled}</span>
        </span>
      );
    }
    // No colon after the name, routable or not: a linked class reads
    // "Wizard 9", so an unroutable domain beside it reads "Envy 9" rather than
    // "Envy: 9", and an unroutable target with no link data — a monster's
    // subtypes — stays a plain term.
    const data = linkDataText(section.attr, target);
    const label = segment && target.slug ? (
      <Link
        to={`/${segment}?slug=${encodeURIComponent(target.slug)}`}
        state={{ from, listFrom }}
        className="stat-block-label"
        style={{ color: 'var(--link)' }}
      >
        {target.name}
      </Link>
    ) : (
      // Body-text colour: `.stat-block-label` is link-coloured, and a term that
      // goes nowhere must not look like one that does (issue 1538).
      <span className="stat-block-label stat-block-term">{target.name}</span>
    );
    return (
      <span key={target.slug || i} className="stat-block-row" style={{ marginBottom: 0 }}>
        {label}
        {data ? <span className="stat-block-value">{data}</span> : null}
      </span>
    );
  };

  return (
    <Block variant={variant} heading={section.heading}>
      <div className="flex flex-wrap gap-x-4 gap-y-1">{targets.map(item)}</div>
    </Block>
  );
}

/**
 * The reverse set BUCKETED by a link-data value, each bucket a filtered-list
 * link (05 §2.2) — "Spells by Level".
 *
 * The shipped card is a hardcoded `[0..9].map(...)` grid rendered behind a
 * speculative `/api/spells?class_name=<slug>&limit=1` probe, because the class
 * row carries no spellcasting flag and the page had no other way to ask
 * (`ClassDetail.js:52-58`, gap G5). Under MRCS the question is already answered
 * by the payload: the reverse section IS the spells that name this class, so a
 * non-caster has an empty set and the section self-hides by the same rule every
 * other section does. The probe is deleted, and with it a second request per
 * class detail.
 *
 * The buckets are the levels the LINKS actually carry, which is the second half
 * of the fix: a class castable only at 1st–4th level renders four buckets where
 * the shipped grid always printed ten, six of them linking to an empty list.
 *
 * Every part of the URL is derived from the mirrored reference (see
 * `reverseBucketRoute`), so nothing here names a spell, a class or a level.
 */
function BucketByLinkData({ section, values, from, listFrom }) {
  const rows = values[section.attr.key];
  if (!Array.isArray(rows) || !rows.length) return null;
  const route = reverseBucketRoute(section.attr.mirrors, getActiveSystem());
  if (!route) return null;

  const self = values.slug;
  const buckets = [...new Set(rows
    .map((row) => row[route.bucketAttr])
    .filter((v) => v !== undefined && v !== null && v !== ''))]
    .sort((a, b) => (Number(a) - Number(b)) || String(a).localeCompare(String(b)));
  if (!buckets.length) return null;

  return (
    <div className="card print:hidden">
      <h2 className="section-header">{section.heading}</h2>
      <ul className="grid grid-cols-2 md:grid-cols-5 gap-1">
        {buckets.map((bucket) => (
          <li key={String(bucket)}>
            <Link
              to={`/${route.segment}?${route.selfKey}=${encodeURIComponent(self)}`
                + `&${route.bucketKey}=${encodeURIComponent(bucket)}`}
              state={{ from, listFrom }}
              className="bucket-link"
            >
              {route.bucketLabel} {bucket}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A reverse set as `<details>` accordions with the source's own prose inside
 * (05 §2.2) — the class page's Archetypes.
 *
 * Which of the source's attributes is the BODY is the source family's own
 * answer — its prose attributes, in its own `display_order` — read off the
 * registry rather than re-declared on the class. An archetype has no page of
 * its own, which is exactly why its text has to travel to the page that does.
 *
 * Closed by default, as the shipped accordions are.
 */
function InlineExpand({ section, values }) {
  const rows = values[section.attr.key];
  if (!Array.isArray(rows) || !rows.length) return null;
  const sourceAttrs = getFamilyByKey(section.attr.targetFamily, getActiveSystem()) || [];
  const bodyAttrs = sourceAttrs.filter((a) => a.isProse);

  return (
    <div className="card">
      <h2 className="section-header">{section.heading} ({rows.length})</h2>
      <div className="space-y-2">
        {rows.map((row, i) => {
          const body = bodyAttrs.find((a) => hasValue(row[a.key]));
          return (
            <details
              key={row.slug || i}
              className="border-b last:border-b-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <summary className="cursor-pointer py-2 font-medium flex items-center justify-between gap-2">
                <span>{row.name}</span>
                {row.rulebook_name && (
                  <span className="text-muted text-xs flex-shrink-0">{row.rulebook_name}</span>
                )}
              </summary>
              {body && (body.datatype === 'richtext' ? (
                <RichText className="prose-content pb-3 pt-1" html={row[body.key]} />
              ) : (
                <div className="text-secondary pb-3 pt-1">{row[body.key]}</div>
              ))}
            </details>
          );
        })}
      </div>
    </div>
  );
}

export default function DetailSection({ section, values, from, listFrom, variant = 'cards' }) {
  if (!IMPLEMENTED_STYLES.has(section.style)) return null;
  if (section.placement === 'subtitle') return null; // rendered in the header
  if (section.style === 'prose') return <Prose section={section} values={values} />;
  if (section.style === 'composed-line') {
    return <ComposedLineSection section={section} values={values} variant={variant} />;
  }
  if (section.style === 'abbrev-list') {
    return <AbbrevList section={section} values={values} variant={variant} />;
  }
  if (section.style === 'link-list') {
    return <LinkList section={section} values={values} from={from} listFrom={listFrom} variant={variant} />;
  }
  if (section.style === 'badge-links') {
    return <BadgeLinks section={section} values={values} from={from} listFrom={listFrom} />;
  }
  if (section.style === 'table') {
    return <TableSection section={section} values={values} />;
  }
  if (section.style === 'signed-list') {
    return <SignedList section={section} values={values} variant={variant} />;
  }
  if (section.style === 'stat-grid') {
    return <StatGrid section={section} values={values} variant={variant} />;
  }
  if (section.style === 'bucket-by-link-data') {
    return <BucketByLinkData section={section} values={values} from={from} listFrom={listFrom} />;
  }
  if (section.style === 'inline-expand') {
    return <InlineExpand section={section} values={values} />;
  }
  return <Rows section={section} values={values} from={from} listFrom={listFrom} variant={variant} />;
}
